/**
 * NodeHeartbeat.js
 * ================
 * Component for monitoring blockchain node health status
 * 
 * Features:
 *   - Real-time node health checking
 *   - Block height monitoring
 *   - Response time measurement
 *   - Visual status indicators
 *   - Multiple node support
 * 
 * Author: Block Explorer Team
 * Date: January 2026
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { Tooltip } from 'primereact/tooltip';
import { APIClient } from '@wharfkit/antelope';
import config from './blockexplorer-config.json';
import './NodeHeartbeat.css';

const NodeHeartbeat = ({ endpoint }) => {
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isExpanded, setIsExpanded] = useState(true);
    const heartbeatInterval = useRef(null);

    useEffect(() => {
        // Initialize nodes from config
        const initialNodes = config.nodeEndpoints.map(node => ({
            ...node,
            status: 'checking',
            blockHeight: null,
            responseTime: null,
            chainId: null,
            version: null,
            lastCheck: null,
            error: null
        }));
        setNodes(initialNodes);

        // Start heartbeat checks
        checkAllNodes(initialNodes);
        heartbeatInterval.current = setInterval(() => {
            setNodes(currentNodes => {
                checkAllNodes(currentNodes);
                return currentNodes;
            });
        }, 10000); // Check every 10 seconds

        return () => {
            if (heartbeatInterval.current) {
                clearInterval(heartbeatInterval.current);
            }
        };
    }, []);

    const checkAllNodes = async (nodesToCheck) => {
        const updatedNodes = await Promise.all(
            nodesToCheck.map(async (node) => {
                return await checkNodeHealth(node);
            })
        );

        setNodes(updatedNodes);
        setLoading(false);
    };

    const checkNodeHealth = async (node) => {
        const startTime = Date.now();
        
        try {
            const client = new APIClient({ url: node.url });
            
            // Get node info
            const info = await client.v1.chain.get_info();
            const responseTime = Date.now() - startTime;

            return {
                ...node,
                status: 'online',
                blockHeight: info.head_block_num,
                responseTime: responseTime,
                chainId: info.chain_id,
                version: info.server_version_string || info.server_version,
                lastCheck: new Date(),
                error: null,
                headBlockTime: info.head_block_time,
                lastIrreversibleBlock: info.last_irreversible_block_num
            };
        } catch (error) {
            return {
                ...node,
                status: 'offline',
                blockHeight: null,
                responseTime: null,
                chainId: null,
                version: null,
                lastCheck: new Date(),
                error: error.message
            };
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'online':
                return 'pi-check-circle';
            case 'offline':
                return 'pi-times-circle';
            case 'checking':
                return 'pi-spin pi-spinner';
            default:
                return 'pi-question-circle';
        }
    };

    const getStatusSeverity = (status) => {
        switch (status) {
            case 'online':
                return 'success';
            case 'offline':
                return 'danger';
            case 'checking':
                return 'warning';
            default:
                return 'secondary';
        }
    };

    const getResponseTimeClass = (responseTime) => {
        if (!responseTime) return '';
        if (responseTime < 200) return 'response-excellent';
        if (responseTime < 500) return 'response-good';
        if (responseTime < 1000) return 'response-fair';
        return 'response-poor';
    };

    const formatResponseTime = (ms) => {
        if (!ms) return 'N/A';
        return `${ms}ms`;
    };

    const formatLastCheck = (date) => {
        if (!date) return 'Never';
        const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h ago`;
    };

    const calculateSyncPercentage = (node) => {
        if (!node.blockHeight || !node.lastIrreversibleBlock) return 100;
        const diff = node.blockHeight - node.lastIrreversibleBlock;
        if (diff <= 0) return 100;
        // Assuming blocks are synced if within 100 blocks
        return Math.max(0, 100 - (diff / 100) * 100);
    };

    const renderNodeCard = (node, index) => {
        const syncPercentage = calculateSyncPercentage(node);

        return (
            <div key={index} className="node-card">
                <div className="node-header">
                    <div className="node-title">
                        <i className={`pi ${getStatusIcon(node.status)} node-icon ${node.status}`}></i>
                        <div>
                            <h4>{node.name}</h4>
                            <p className="node-url">{node.url}</p>
                        </div>
                    </div>
                    <Tag 
                        severity={getStatusSeverity(node.status)} 
                        value={node.status.toUpperCase()}
                        className="status-tag"
                    />
                </div>

                {node.status === 'online' && (
                    <div className="node-stats">
                        <div className="stat-row">
                            <div className="stat-item">
                                <i className="pi pi-box"></i>
                                <div className="stat-details">
                                    <span className="stat-label">Block Height</span>
                                    <span className="stat-value">
                                        #{node.blockHeight?.toLocaleString()}
                                    </span>
                                </div>
                            </div>

                            <div className="stat-item">
                                <i className="pi pi-clock"></i>
                                <div className="stat-details">
                                    <span className="stat-label">Response Time</span>
                                    <span className={`stat-value ${getResponseTimeClass(node.responseTime)}`}>
                                        {formatResponseTime(node.responseTime)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="stat-row">
                            <div className="stat-item">
                                <i className="pi pi-server"></i>
                                <div className="stat-details">
                                    <span className="stat-label">Version</span>
                                    <span className="stat-value version">
                                        {node.version || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div className="stat-item">
                                <i className="pi pi-check-square"></i>
                                <div className="stat-details">
                                    <span className="stat-label">Irreversible</span>
                                    <span className="stat-value">
                                        #{node.lastIrreversibleBlock?.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {syncPercentage < 100 && (
                            <div className="sync-progress">
                                <div className="sync-header">
                                    <span className="sync-label">Sync Progress</span>
                                    <span className="sync-percentage">{syncPercentage.toFixed(1)}%</span>
                                </div>
                                <ProgressBar 
                                    value={syncPercentage} 
                                    showValue={false}
                                    className="sync-bar"
                                />
                            </div>
                        )}

                        <div className="node-footer">
                            <span className="last-check">
                                <i className="pi pi-refresh"></i>
                                Last checked: {formatLastCheck(node.lastCheck)}
                            </span>
                        </div>
                    </div>
                )}

                {node.status === 'offline' && (
                    <div className="node-error">
                        <i className="pi pi-exclamation-circle"></i>
                        <p className="error-message">
                            {node.error || 'Node is currently offline or unreachable'}
                        </p>
                        <span className="last-check">
                            Last checked: {formatLastCheck(node.lastCheck)}
                        </span>
                    </div>
                )}

                {node.status === 'checking' && (
                    <div className="node-checking">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Checking node status...</p>
                    </div>
                )}
            </div>
        );
    };

    const getOverallStatus = () => {
        if (loading) return { label: 'Checking', severity: 'warning', icon: 'pi-spin pi-spinner' };
        
        const onlineCount = nodes.filter(n => n.status === 'online').length;
        const totalCount = nodes.length;

        if (onlineCount === totalCount) {
            return { label: 'All Systems Operational', severity: 'success', icon: 'pi-check-circle' };
        } else if (onlineCount > 0) {
            return { 
                label: `${onlineCount}/${totalCount} Nodes Online`, 
                severity: 'warning', 
                icon: 'pi-exclamation-triangle' 
            };
        } else {
            return { label: 'All Nodes Offline', severity: 'danger', icon: 'pi-times-circle' };
        }
    };

    const overallStatus = getOverallStatus();

    return (
        <Card className="heartbeat-card">
            <div className="heartbeat-header">
                <div className="heartbeat-title-section">
                    <i className="pi pi-heart-fill heartbeat-icon"></i>
                    <div>
                        <h3 className="heartbeat-title">Node Health Status</h3>
                        <p className="heartbeat-subtitle">Real-time blockchain node monitoring</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <Tag 
                        severity={overallStatus.severity}
                        icon={`pi ${overallStatus.icon}`}
                        value={overallStatus.label}
                        className="overall-status-tag"
                    />
                    <Button
                        icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsExpanded(prev => !prev);
                        }}
                        className="p-button-rounded p-button-text"
                        tooltip={isExpanded ? 'Collapse' : 'Expand'}
                        tooltipOptions={{ position: 'top' }}
                        aria-label={isExpanded ? 'Collapse node details' : 'Expand node details'}
                    />
                </div>
            </div>

            {isExpanded && (
                <div className="nodes-grid">
                    {nodes.map((node, index) => renderNodeCard(node, index))}
                </div>
            )}
        </Card>
    );
};

export default NodeHeartbeat;
